import {HttpErrorResponse, HttpInterceptorFn} from '@angular/common/http';
import {catchError, of, switchMap, throwError} from "rxjs";
import {inject, Injector} from "@angular/core";
import {AuthService} from "../../services/auth/auth";
import {environment} from "../../../environments/environment";
import {TokenRefreshResponseModel} from "../../models/auth.model";

export const refreshTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const apiUrl: string = environment.apiBaseUrl.replace(/\/$/, '');
  const skipRefresh =
    !req.url.startsWith(apiUrl) ||
    req.url.includes('/login') ||
    req.url.includes('/register') ||
    req.url.includes('/refresh') ||
    req.url.includes('/logout') ||
    req.url.includes('/assets/i18n') ||
    req.url.includes('assets/i18n');

  if (skipRefresh) {
    return next(req);
  }

  const injector: Injector = inject(Injector);
  const authService: AuthService = injector.get(AuthService);
  const sessionGeneration = authService.sessionGeneration;

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      if (authService.isIdentityChanging) return throwError(() => error);

      if (sessionGeneration !== authService.sessionGeneration) return throwError(() => error);

      // Never replay a user's failed operation with administrator credentials.
      if (authService.isImpersonating) {
        return authService.endImpersonation().pipe(switchMap(() => throwError(() => error)));
      }

      // A delayed 401 may belong to the token another request has already refreshed.
      const currentToken = authService.getToken;
      const refresh = () => req.url.includes('/change-password')
        ? authService.refreshToken(true)
        : authService.refreshToken();
      const token$ = currentToken && req.headers.get('Authorization') !== `Bearer ${currentToken}`
        ? of({token: currentToken})
        : refresh().pipe(
          catchError(refreshError => {
            void authService.clearSession(sessionGeneration);
            return throwError(() => refreshError);
          }),
        );

      return token$.pipe(
        switchMap((newToken: Pick<TokenRefreshResponseModel, 'token'>) => next(req.clone({
          headers: req.headers.set('Authorization', `Bearer ${newToken.token}`),
        })).pipe(
          catchError(retryError => {
            // A normal API error after a successful refresh does not invalidate the session.
            if (retryError.status === 401) void authService.clearSession(sessionGeneration);
            return throwError(() => retryError);
          }),
        )),
      );
    })
  );
};
